import React, { lazy } from 'react';

// Routes
import {
  RunSheet,
  Settings,
  Floorplan,
  Timeline,
  EditFloorplan,
  Integrations,
  Guestbook,
  MarketPlaceIntegrations,
  Calendar,
} from '@eatclub-apps/ec-bookings-library';
import { Redirect } from 'react-router-dom';
import { BalanceHistory } from '../components/balanceHistory';
import BookingsLandingPageTitle from '../components/liveBookings/BookingsLanding/BookingsLandingPageTitle';

import CircleCheckIcon from '../assets/icons/circle_check.svg';
import CustomerValueHeader from '../screens/analytics/CustomerValue/CustomerValueHeader';
import CancelMembership from '../screens/cancelMembership/CancelMembership';
import { FEATURE_SCOPES } from '../utils/permissions';

const Analytics = lazy(() => import('../components/analytics/Analytics'));
const Logout = lazy(() => import('../components/auth/Logout'));
const Billing = lazy(() => import('../components/billing/Billing'));
const Bookings = lazy(() => import('../components/bookings/Bookings'));
const EditDeal = lazy(() => import('../components/deals/editDeal/EditDeal'));
const EditOffer = lazy(() => import('../components/deals/editOffer/EditOffer'));
const BookingsLanding = lazy(
  () => import('../components/liveBookings/BookingsLanding/BookingsLanding'),
);
const AudienceTargeting = lazy(() => import('../screens/audienceTargeting/AudienceTargeting'));
const PayBalance = lazy(() => import('../components/payBalance/PayBalance'));
// const BookingsLandingPageTitle = lazy(
//   () => import('../components/liveBookings/BookingsLanding/BookingsLandingPageTitle'),
// );

const Menus = lazy(() => import('../components/menus/Menus'));
const NotFound = lazy(() => import('../components/notFound/NotFound'));
const AllOffers = lazy(() => import('../components/offers/AllOffers/AllOffers'));
const CreateGoal = lazy(() => import('../components/offers/CreateGoal/CreateGoal'));
const EditGoal = lazy(() => import('../components/offers/EditGoal/EditGoal'));
const ViewGoal = lazy(() => import('../components/offers/ViewGoal/ViewGoal'));
const Offers = lazy(() => import('../components/offers/OffersDashboard/Offers'));
const PreviousGoalsScreen = lazy(() => import('../components/offers/PreviousGoalsScreen'));
const Support = lazy(() => import('../components/support/Support'));
const HelpCentre = lazy(() => import('../components/HelpCentre/HelpCentre'));
const Profile = lazy(() => import('../components/profile/Profile'));
const BillingDetails = lazy(() => import('../screens/profile/BillingDetails'));
const Reactivate = lazy(() => import('../screens/reactivate/Reactivate'));
const Settlements = lazy(() => import('../components/settlements/Settlements'));
const Insight = lazy(() => import('../screens/analytics/Insight'));
const CustomerValue = lazy(() => import('../screens/analytics/CustomerValue/CustomerValue'));
const BusinessReview = lazy(() => import('../screens/businessReview/BusinessReview'));
const CreateOffer = lazy(() => import('../components/offers/CreateOffer/CreateOffer'));
const EditRequestSuccess = lazy(() => import('../components/offers/EditRequestSuccess'));
const OnboardingTour = lazy(() => import('../screens/onboardingTour/OnboardingTour'));
const HolidayHours = lazy(() => import('../screens/holidayHours/HolidayHours'));

const routes = [
  // Unused analytics kept in just in case we still need it
  {
    path: '/analytics_old',
    pageTitle: 'Analytics',
    exact: true,
    blockWhiteLabel: false,
    main: () => <Analytics />,
    allowedScopes: FEATURE_SCOPES.INSIGHTS,
  },

  // Analytics now just redirects to insights
  {
    path: '/analytics',
    pageTitle: 'Analytics',
    exact: true,
    blockWhiteLabel: false,
    ecPayEnabledRequired: false,
    main: () => <Redirect to='/insights' />,
    allowedScopes: FEATURE_SCOPES.INSIGHTS,
  },
  {
    path: '/insights',
    pageTitle: 'Insight',
    exact: true,
    blockWhiteLabel: false,
    ecPayEnabledRequired: false,
    showDebtBanner: true,
    main: () => <Insight />,
    allowedScopes: FEATURE_SCOPES.INSIGHTS,
  },
  {
    path: '/customer-value',
    pageTitle: 'Customer value',
    exact: true,
    blockWhiteLabel: false,
    ecPayEnabledRequired: false,
    showDebtBanner: true,
    main: () => <CustomerValue />,
    customHeader: <CustomerValueHeader />,
  },
  // {
  //   path: '/deals',
  //   pageTitle: 'Deals',
  //   exact: true,
  //   blockWhiteLabel: true,
  //   main: () => <Deals />,
  // },
  // {
  //   path: '/deals/create',
  //   pageTitle: 'Create Deal',
  //   exact: true,
  //   blockWhiteLabel: true,
  //   backNavigation: {
  //     label: 'Back',
  //     route: '/offers',
  //   },
  //   main: () => <CreateDeal />,
  // },
  {
    path: '/deals/edit/:dealId',
    pageTitle: 'Edit Deal',
    exact: false,
    blockWhiteLabel: false,
    main: () => <EditDeal />,
    allowedScopes: FEATURE_SCOPES.OFFERS,
  },
  {
    path: '/offers',
    pageTitle: 'Exclusive offers',
    exact: true,
    blockWhiteLabel: true,
    main: () => <Offers />,
  },
  {
    path: '/offers/active',
    pageTitle: 'All offers',
    exact: true,
    blockWhiteLabel: true,
    backNavigation: {
      label: 'Back',
      route: '/offers',
      goBack: true,
    },
    main: () => <AllOffers />,
  },
  {
    path: '/offers/create',
    pageTitle: 'Create new offers',
    exact: true,
    blockWhiteLabel: true,
    backNavigation: {
      label: 'Back',
      route: '/offers',
      goBack: true,
    },
    main: () => <CreateOffer />,
    allowedScopes: FEATURE_SCOPES.OFFERS,
  },
  {
    path: '/offers/edit/request/success',
    pageIcon: <CircleCheckIcon />,
    pageTitle: 'Request sent!',
    exact: false,
    blockWhiteLabel: false,
    main: () => <EditRequestSuccess />,
    allowedScopes: FEATURE_SCOPES.OFFERS,
  },
  {
    path: '/offers/edit/:dealId',
    pageTitle: 'Edit Offer',
    exact: false,
    blockWhiteLabel: false,
    backNavigation: {
      label: 'Back',
      route: '/offers',
      goBack: true, // If true, try to go back to the previous page in stack before the hardcoded route
    },
    main: () => <EditOffer />,
    allowedScopes: FEATURE_SCOPES.OFFERS,
  },
  {
    path: '/goals/previous',
    pageTitle: 'Previous Goals',
    exact: true,
    blockWhiteLabel: false,
    backNavigation: {
      label: 'Back to offers',
      route: '/offers',
    },
    main: () => <PreviousGoalsScreen />,
  },
  {
    path: '/goals/create',
    pageTitle: '',
    exact: true,
    blockWhiteLabel: false,
    main: () => <CreateGoal />,
    allowedScopes: FEATURE_SCOPES.OFFERS,
  },
  {
    path: '/goals/edit/:goalId',
    pageTitle: 'Edit Goal',
    exact: false,
    blockWhiteLabel: false,
    backNavigation: {
      label: 'Back',
      route: '/offers',
    },
    main: () => <EditGoal />,
    allowedScopes: FEATURE_SCOPES.OFFERS,
  },
  {
    path: '/goals/:goalId',
    pageTitle: '',
    exact: false,
    blockWhiteLabel: false,
    backNavigation: {
      label: 'Back',
      route: '/offers',
    },
    main: () => <ViewGoal />,
  },
  {
    path: '/transactions',
    pageTitle: 'Transactions',
    exact: false,
    blockWhiteLabel: false,
    main: () => <Bookings />,
    allowIfDisabled: true,
    allowedScopes: FEATURE_SCOPES.TRANSACTIONS,
  },
  {
    path: '/settlements',
    pageTitle: 'Settlements',
    exact: true,
    blockWhiteLabel: false,
    ecPayEnabledRequired: true,
    main: () => <Settlements />,
    allowIfDisabled: true,
    allowedScopes: FEATURE_SCOPES.SETTLEMENTS,
  },
  {
    // todo: add in redirect from billing to settlements when ecpay is enabled for all regions
    path: '/billing',
    pageTitle: 'Billing & Invoices',
    exact: false,
    blockWhiteLabel: false,
    main: () => <Billing />,
    allowIfDisabled: true,
    allowedScopes: FEATURE_SCOPES.SETTLEMENTS,
  },
  {
    path: '/settlements/historical-statements',
    pageTitle: 'Billing & Invoices',
    exact: true,
    blockWhiteLabel: false,
    blockPartners: true,
    main: () => <Billing />,
    allowIfDisabled: true,
    allowedScopes: FEATURE_SCOPES.SETTLEMENTS,
  },
  {
    path: '/settlements/balance-history',
    pageTitle: 'Balance history',
    exact: true,
    blockWhiteLabel: false,
    ecPayEnabledRequired: true,
    main: () => <BalanceHistory />,
    backNavigation: {
      label: 'Back',
      route: '/settlements',
    },
    allowIfDisabled: true,
    allowedScopes: FEATURE_SCOPES.SETTLEMENTS,
  },
  {
    path: '/settlements/pay-balance',
    pageTitle: '',
    exact: true,
    blockWhiteLabel: false,
    ecPayEnabledRequired: true,
    main: () => <PayBalance />,
    backNavigation: {
      label: 'Back',
      route: '/settlements/balance-history',
    },
    allowIfDisabled: true,
    allowedScopes: FEATURE_SCOPES.SETTLEMENTS,
  },
  {
    path: '/support',
    pageTitle: 'Support',
    exact: false,
    blockWhiteLabel: false,
    main: () => <Support />,
    allowIfDisabled: true,
    allowedScopes: FEATURE_SCOPES.HELP_CENTRE,
  },
  {
    path: '/help-centre',
    pageTitle: 'Help Centre',
    exact: false,
    blockWhiteLabel: false,
    main: () => <HelpCentre />,
    allowIfDisabled: true,
    allowedScopes: FEATURE_SCOPES.HELP_CENTRE,
  },
  {
    path: '/menus',
    pageTitle: 'Menus',
    exact: true,
    blockWhiteLabel: false,
    main: () => <Menus />,
    allowedScopes: FEATURE_SCOPES.MENUS,
  },
  {
    path: '/audience-targeting',
    pageTitle: 'Audience targeting',
    exact: false,
    blockWhiteLabel: false,
    main: () => <AudienceTargeting />,
    allowedScopes: FEATURE_SCOPES.AUDIENCE_TARGETING,
  },
  {
    path: '/livebookings',
    pageTitle: 'EatClub Bookings',
    exact: true,
    blockWhiteLabel: false,
    customPageTitle: () => <BookingsLandingPageTitle />,
    main: () => <BookingsLanding />,
    allowedScopes: FEATURE_SCOPES.BOOKINGS,
    allowIfDisabled: true,
  },
  {
    path: '/livebookings/runsheet',
    pageTitle: 'Run sheet',
    id: 'runsheet',
    exact: true,
    blockWhiteLabel: false,
    header: 'obee',
    obeeEnabledRequired: true,
    main: () => <RunSheet />,
    allowedScopes: FEATURE_SCOPES.BOOKINGS,
  },
  {
    path: '/livebookings/monthly',
    pageTitle: 'Monthly',
    id: 'monthly',
    exact: true,
    blockWhiteLabel: false,
    header: 'obee',
    obeeEnabledRequired: true,
    main: () => <Calendar />,
    allowedScopes: FEATURE_SCOPES.BOOKINGS,
  },
  {
    path: '/livebookings/floorplan',
    pageTitle: 'Floor Plan',
    id: 'floorplan',
    exact: true,
    blockWhiteLabel: false,
    header: 'obee',
    obeeEnabledRequired: true,
    obeePremiumRequired: true,
    main: () => <Floorplan />,
    allowedScopes: FEATURE_SCOPES.BOOKINGS,
  },
  {
    path: '/livebookings/floorplan/edit',
    pageTitle: 'Edit Floor Plan',
    id: 'editFloorplan',
    exact: true,
    blockWhiteLabel: false,
    header: 'custom',
    hideNavigation: true,
    forceHideNavigation: true, // No option to show on this screen
    obeeEnabledRequired: true,
    main: () => <EditFloorplan />,
    allowedScopes: FEATURE_SCOPES.BOOKINGS,
  },
  {
    path: '/livebookings/timeline',
    pageTitle: 'Timeline',
    id: 'timeline',
    exact: true,
    blockWhiteLabel: false,
    header: 'obee',
    obeeEnabledRequired: true,
    obeePremiumRequired: true,
    main: () => <Timeline />,
    allowedScopes: FEATURE_SCOPES.BOOKINGS,
  },
  {
    path: '/livebookings/guestbook',
    pageTitle: 'Guest book',
    id: 'guestbook',
    exact: true,
    blockWhiteLabel: false,
    header: 'obee',
    obeeEnabledRequired: true,
    obeePremiumRequired: true,
    main: () => <Guestbook />,
    allowedScopes: FEATURE_SCOPES.BOOKINGS,
  },
  // {
  //   path: '/livebookings/calendar',
  //   pageTitle: 'Calendar',
  //   exact: true,
  //   blockWhiteLabel: false,
  //   header: 'obee',
  //   main: () => <Calendar />,
  // },
  {
    path: '/livebookings/settings',
    pageTitle: 'Settings',
    exact: true,
    blockWhiteLabel: false,
    header: 'custom',
    hideNavigation: true,
    obeeEnabledRequired: true,
    forceHideNavigation: true,
    main: () => <Settings />,
    allowedScopes: FEATURE_SCOPES.BOOKINGS,
  },
  {
    path: '/livebookings/integrations/fbmarketplace',
    pageTitle: 'Bookings integrations',
    exact: true,
    blockWhiteLabel: false,
    header: 'custom',
    hideNavigation: true,
    obeeEnabledRequired: true,
    forceHideNavigation: true,
    main: () => <MarketPlaceIntegrations />,
    allowedScopes: FEATURE_SCOPES.BOOKINGS,
    allowIfDisabled: true,
  },
  {
    path: '/livebookings/integrations',
    pageTitle: 'Bookings integrations',
    exact: true,
    blockWhiteLabel: false,
    header: 'custom',
    hideNavigation: true,
    obeeEnabledRequired: true,
    forceHideNavigation: true,
    main: () => <Integrations />,
    allowedScopes: FEATURE_SCOPES.BOOKINGS,
  },
  {
    path: '/logout',
    pageTitle: 'Logout',
    blockWhiteLabel: false,
    header: 'custom',
    main: () => <Logout />,
  },
  {
    path: '/profile',
    pageTitle: 'Profile',
    exact: true,
    blockWhiteLabel: false,
    main: () => <Profile />,
    allowedScopes: FEATURE_SCOPES.PROFILE,
  },
  {
    path: '/profile/billing',
    pageTitle: 'Billing details',
    exact: true,
    blockWhiteLabel: false,
    main: () => <BillingDetails />,
    allowedScopes: FEATURE_SCOPES.PROFILE, // Only admins/managers should be able to access this
  },
  {
    path: '/reactivate',
    pageTitle: 'Reactivate account',
    exact: true,
    blockWhiteLabel: false,
    main: () => <Reactivate />,
    allowedScopes: FEATURE_SCOPES.PROFILE, // Only admins/managers should be able to access this
  },
  {
    path: '/tour',
    pageTitle: 'Onboarding tour',
    blockWhiteLabel: false,
    main: () => <OnboardingTour />,
    hideHeader: true,
    hideMobileHeader: true,
    hideNavigation: true,
    forceHideNavigation: true,
    allowHideNavigation: true,
    allowIfDisabled: true,
    showDisabledBanner: false, // Prevent showing the disabled banner on this screen
  },
  {
    path: '/business-review',
    pageTitle: 'Quarterly business review',
    exact: true,
    blockWhiteLabel: false,
    ecPayEnabledRequired: false,
    showDebtBanner: false,
    hideNavigation: true,
    // hideHeader: true,
    // hideMobileHeader: true,
    // forceHideNavigation: false,
    // allowHideNavigation: true,
    header: 'custom',
    main: () => <BusinessReview />,
  },
  {
    path: '/profile/holiday-hours',
    pageTitle: 'Holiday hours',
    exact: true,
    blockWhiteLabel: false,
    ecPayEnabledRequired: false,
    showDebtBanner: false,
    main: () => <HolidayHours />,
  },
  {
    path: '/cancel',
    pageTitle: 'Cancel membership',
    blockWhiteLabel: false,
    main: () => <CancelMembership />,
    hideHeader: true,
  },
  {
    path: '*',
    pageTitle: '',
    exact: false,
    blockWhiteLabel: false,
    main: () => <NotFound />,
  },
];

export default routes;
